import React, { useState, useEffect } from 'react';
import Link from '@mui/material/Link';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Card,
  CardHeader,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  InputAdornment,
} from '@mui/material';
import { IconButton, Collapse } from '@mui/material';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from "@mui/icons-material/Close";

import Button from '@mui/material/Button';
import Title from '../../components/Title';
import SearchBar from '../../components/SearchBar';
import EditModal from '../../components/EditModal';
import AddModal from '../../components/AddModal';
import UploadModal from '../../components/UploadModal';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, NA_LABEL } from '../../common/AppConstants';

const TITLE = 'Classrooms';
const API_URL = 'campus/classroom/';
const UPLOAD_TEMPLATE = 'classroom_upload_template.xlsx';
const NO_DATA_AVAILABLE_STRING =
  'No data available, please start adding now !!!';

function preventDefault(event) {
  event.preventDefault();
}

export default function Rooms() {
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [relatedData, setRelatedData] = useState({});
  const [selectedEditRow, setSelectedEditRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtered data based on search
  const filteredRows = rows.filter(row_item =>
    row_item.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddClick = () => {
    setIsAddModalOpen(true);
  };

  const handleAddModalClose = () => {
    setIsAddModalOpen(false);
  };

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleEditClick = (row) => {
    console.log('Edit row ', row);
    setSelectedEditRow(row);
  };

  useEffect(() => {
    if (selectedEditRow !== null && selectedEditRow !== undefined) {
      // Only open the modal after the row has been selected
      setIsEditModalOpen(true);
    }
  }, [selectedEditRow]); // Runs when selectedEditRow changes

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedEditRow(null);
  };

  const handleEditSave = async (updatedEditRow, onEditFailure) => {
    await httpRequest({
      method: 'PUT',
      url: API_URL,
      data: updatedEditRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.map((row, index) =>
            row.id === selectedEditRow.id ? { ...row, ...data } : row,
          ),
        );
        handleEditModalClose();
      },
      onFailure: (data) => {
        onEditFailure(data);
      },
      addNotification,
    });
  };

  const handleDelete = async (deletedRow) => {
    await httpRequest({
      method: 'DELETE',
      url: API_URL,
      data: deletedRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.filter((row) => row.id !== deletedRow.id),
        );
      },
      addNotification,
    });
  };

  const handleAddSave = async (addFormData, onAddSuccess, onAddFailure) => {
    await httpRequest({
      method: 'POST',
      url: API_URL,
      data: addFormData,
      onSuccess: (data) => {
        handleAddModalClose();
        setRows((prevRows) => [data.entry, ...prevRows]);
        handleAddClick();
        onAddSuccess();
      },
      onFailure: (data) => {
        onAddFailure(data);
      },
      addNotification,
    });
  };

  const handleUpload = async (file, onUploadSuccess, onUploadFailure) => {
    const formData = new FormData();
    formData.append('file', file);

    await httpRequest({
      method: 'POST',
      url: `${API_URL}upload/`,
      data: formData,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
      onSuccess: (data) => {
        // setRows((prevRows) => [...prevRows, response.data.entry]);
        refreshData();
        onUploadSuccess(data);
      },
      onFailure: (data) => {
        onUploadFailure(data);
      },
      addNotification,
    });
  };


  const handleFilterApply = async () => {
    // ToDo: Filter table data
  };

  useEffect(() => {
    const fetchData = async () => {
      refreshData();
    };
    fetchData();
  }, []);

  const refreshData = async (event) => {
    setIsLoading(true);

    await httpRequest({
      method: 'GET',
      url: API_URL,
      onSuccess: (data) => {
        setColumns(data.columns);
        setRows(data.rows);
        setRelatedData(data.relatedData);
      },
      onFailure: (data) => {
        setColumns([]);
        setRows([]);
        setRelatedData({});
      },
      addNotification,
    });
    
    setIsLoading(false);
  };

  const ClassroomCard = ({ classroom_row, classroom_dict }) => {
    const [expanded, setExpanded] = useState(false);

    const handleExpandClick = () => {
      setExpanded((prev) => !prev);
    };

    return (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          transition: 'box-shadow 0.3s',
          '&:hover': {
            boxShadow: 3
          }
        }
      }>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5">{classroom_dict['name']}</Typography>
              {/* More data chips if needed */}
            </Box>
          }
          action={
            <IconButton onClick={() => handleEditClick(classroom_dict)} size="small">
              <EditTwoToneIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            px: 2,
            py: 0,
            alignItems: 'center',
            '& .MuiCardHeader-action': {
              alignSelf: 'center',
            },
          }}
          slotProps={{
            content: { sx: { py: 1.5 } },
          }}
        />
        {/* <Divider/> */}
        
        <CardContent sx={{ pt: 0, pb: 1, flexGrow: 1, display: 'flex' }}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 0.75, pt: 2, pb: 2 }}>
            {/* <Typography variant="body2">
              <Typography component="span" variant="body2" fontWeight="bold">Classroom Type:</Typography> {relatedData['classroom_type'][classroom_dict['classroom_type']]['label'] ?? NA_LABEL}
            </Typography>
            <Typography variant="body2">
              <Typography component="span" variant="body2" fontWeight="bold">Department:</Typography> {relatedData?.['department']?.[classroom_dict['department']]?.['label'] ?? NA_LABEL}
            </Typography> */}
          </Box>

          {/* Expand Icon */}
          <CardActions disableSpacing sx={{ p: 0}}>
            {/* <IconButton
              size="small"
              onClick={handleExpandClick}
              sx={{ alignSelf: 'flex-start', mt: 1}}
            >
              <ExpandCircleDownOutlinedIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}
              />
            </IconButton> */}
          </CardActions>
        </CardContent>

        {/* Collapsible Content */}
        <Collapse in={expanded}>
          <Divider sx={{ borderStyle: 'dashed' }} />
          <CardContent sx={{ bgcolor: '#FBFBFB' }}>
            <Stack spacing={1}>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Seating Capacity:</Typography> {classroom_dict['seating_capacity'] ?? NA_LABEL}
              </Typography>
            </Stack>
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Title>{TITLE}</Title>
          </Box>
          {/* Search Bar */}
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {/* <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadClick}
              sx={{
                px: 4,
                display: { md: 'flex', xs: 'none' },
              }}
            >
              Upload
            </Button> */}
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={handleAddClick}
              sx={{
                px: 4,
              }}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Scrollable table container */}
      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          // mt: 2,
          minHeight: 200, // Ensures proper space for loader
          display: 'flex',
          flexDirection: 'column', // Aligns table to the top when shown
          alignItems: isLoading ? 'center' : 'stretch', // Center loader, but stretch table
          justifyContent: isLoading ? 'center' : 'flex-start', // Center loader, start table at top
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            {rows.length === 0 ? (
              // Display message when there are no rows
              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  py: 2,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)',
                    xl: 'repeat(3, 1fr)',
                  },
                  gap: 1.5, // This creates even spacing without edge padding
                }}
              >
                {filteredRows.map((item_dict, index) => (
                  <Box key={index}>
                    <ClassroomCard
                      classroom_row={index}
                      classroom_dict={item_dict}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Fade>
        )}
      </Box>

      {/* Add Classroom Modal */}
      <Box>
        <AddModal
          open={isAddModalOpen}
          columns={columns}
          relatedData={relatedData}
          rows={rows}
          handleClose={handleAddModalClose}
          handleSave={handleAddSave}
          add_title="Add Classroom"
        />
      </Box>

      {/* Upload Modal */}
      <UploadModal
        open={isUploadModalOpen}
        uploadTemplate={UPLOAD_TEMPLATE}
        handleClose={handleUploadModalClose}
        handleUpload={handleUpload}
      />

      {/* Edit Classroom Modal */}
      <Box>
        <EditModal
          open={isEditModalOpen}
          columns={columns}
          rowData={selectedEditRow}
          relatedData={relatedData}
          handleClose={handleEditModalClose}
          handleSave={handleEditSave}
          handleDelete={handleDelete}
          edit_title="Edit Classroom"
        />
      </Box>
    </Box>
  );
}
